"use client"

import type { ElementType } from "react"

import { useTranslations } from "next-intl"

import { ArrowDisc, Heading, Reel, Text } from "@/components/ui"
import { TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"
import { useReelPagination } from "@/lib/hooks/use-reel-pagination"

export interface PromoCardItem {
  id: string
  eyebrow?: string
  title: string
  image: string
  imageAlt?: string
  href?: string
  serveId?: string
}

const ARROW = "absolute top-1/2 z-10 -translate-y-1/2"

export const PromoCards = ({ items, priority = false }: { items: PromoCardItem[]; priority?: boolean }) => {
  const { ref, overflowing, atStart, atEnd, page } = useReelPagination(items.length)
  const t = useTranslations("marketplace.product")
  return (
    <div className="relative">
      <Reel ref={ref} gap="500" snap inset>
        {items.map((item, index) => {
          const Card: ElementType = item.href ? Link : "div"
          return (
            <Card
              key={item.id}
              {...(item.href ? { href: item.href } : {})}
              data-nelo-serve={item.serveId}
              className="press aspect-promo-card rounded-12x bg-surface-muted focus-visible:ring-brand relative w-56 shrink-0 snap-start overflow-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none @2xl:w-76"
            >
              <TileImage src={item.image} alt={item.imageAlt ?? ""} fill sizes="(max-width: 768px) 224px, 304px" priority={priority && index < 2} className="object-cover" />
              <span aria-hidden className="bg-scrim-card-top pointer-events-none absolute inset-0" />
              <div className="text-fg-inverted relative flex flex-col p-350 @2xl:p-500">
                {item.eyebrow && (
                  <Text as="span" variant="body" family="text" tone="inverted" className="text-copy @2xl:text-copy-lg">
                    {item.eyebrow}
                  </Text>
                )}
                <Heading as="h3" family="hero" tone="inverted" className="text-promo-title @2xl:text-promo-title-lg">
                  {item.title}
                </Heading>
              </div>
            </Card>
          )
        })}
      </Reel>
      {overflowing && !atStart && (
        <ArrowDisc direction="previous" aria-label={t("previous")} onClick={() => page(-1)} className={`${ARROW} @3xl:left-gut left-300 @3xl:-translate-x-1/2`} />
      )}
      {overflowing && !atEnd && <ArrowDisc direction="next" aria-label={t("next")} onClick={() => page(1)} className={`${ARROW} @3xl:right-gut right-300 @3xl:translate-x-1/2`} />}
    </div>
  )
}
